import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { app, getAuthInstance } from '@/lib/firebase';
import { usePaulAccess } from '@/lib/paulAccess';
import { signInWithEmailPassword, createUserWithEmailPassword, signInWithGoogle, signOut } from '@/lib/auth';
import { listCollections, addToQueue, addToCollection, createCollection, recordHistory, type UserCollection } from '@/lib/collections';
import { useEventsForRange } from '@/hooks/useEventsForRange';
import type { CalendarEvent } from '@/lib/eventStore';
import { updateEvent } from '@/lib/eventStore';
import { Globe, Lock } from 'lucide-react';
import {
  getMonth13, getCurrentGalacticYear, getToday13, day13ToGregorian,
} from '@/lib/calendar13';
import { MONTH_NAMES, PLASMA_NAMES } from '@/lib/calendar13Types';
import type { Day13 } from '@/lib/calendar13Types';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

const keyOf = (d: Date) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const isoKey = (d: Date) => d.toISOString().split('T')[0];

type MinedEvent = { id: string; title?: string; eventDateKey?: string; datePrecision?: string; confidence?: number; channelId?: string; channelTitle?: string; summary?: string; videoId?: string; sourceTimestampSec?: number; sourceTimestampUrl?: string; sourceQuote?: string };

const fmtClock = (s?: number): string => {
  if (s == null || !isFinite(s)) return '';
  const t = Math.floor(s), m = Math.floor(t / 60), sec = t % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

const functionsOrigin = (): string => {
  const projectId =
    (app as { options?: { projectId?: string } } | undefined)?.options?.projectId ||
    (import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined) || 'studio-8849194767-f8721';
  return `https://us-central1-${projectId}.cloudfunctions.net`;
};

// Load the mined events for the Gregorian month(s) the visible moon spans (1–2 months) — scoped so
// we never pull the whole year, and month-prefix queries also catch multi-day (month-precision)
// events that partially overlap the moon. Returns a flat list (day + month precision).
async function fetchMinedForMonths(months: string[]): Promise<MinedEvent[]> {
  const out: MinedEvent[] = [];
  try {
    const lists = await Promise.all(months.map(async (m) => {
      const res = await fetch(`${functionsOrigin()}/getTimelineByDate?date=${encodeURIComponent(m)}`);
      if (!res.ok) return [] as MinedEvent[];
      const j = await res.json();
      return (j.events || []) as MinedEvent[];
    }));
    const seen = new Set<string>();
    for (const ev of lists.flat()) {
      if (!ev.eventDateKey || !ev.id || seen.has(ev.id)) continue;
      if (ev.datePrecision !== 'day') continue; // single-day events only
      seen.add(ev.id);
      out.push(ev);
    }
  } catch { /* ignore */ }
  return out;
}

type Flat = { gi: number; moon: number; day: number; greg: Date; d13: Day13 };
type Span = { id: string; title: string; startGi: number; endGi: number; kind: 'cal' | 'mined'; openGi: number; color?: string };

// Readable text colour (dark/light) for a given background hex.
const textOn = (hex: string): string => {
  const h = (hex || '').replace('#', '');
  if (h.length < 6) return '#111827';
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#111827' : '#ffffff';
};
type Seg = { span: Span; colStart: number; colEnd: number; contL: boolean; contR: boolean; lane: number };

const DAY_ROW_TOP = 50;   // px reserved at top of a week row for the day number
const LANE_H = 30;        // px per event lane
const MAX_LANES = 5;

export default function ThirteenCalendar() {
  const [galacticYear, setGalacticYear] = useState<number>(() => getCurrentGalacticYear());
  const [moonIndex, setMoonIndex] = useState<number>(() => { const t = getToday13(); return !t.isOutOfTime ? t.month : 1; });
  const [selected, setSelected] = useState<{ d13: Day13; cal: CalendarEvent[]; mined: MinedEvent[] } | null>(null);
  const [mined, setMined] = useState<MinedEvent[]>([]);
  const [channelMeta, setChannelMeta] = useState<Record<string, { color?: string; title?: string; handle?: string; thumbnailUrl?: string }>>({});
  const { isPaul, email } = usePaulAccess();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'join'>('join');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authErr, setAuthErr] = useState<string | null>(null);
  const [authBusy, setAuthBusy] = useState(false);
  const submitAuth = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setAuthErr(null); setAuthBusy(true);
    try {
      if (authMode === 'join') await createUserWithEmailPassword(authEmail.trim(), authPassword);
      else await signInWithEmailPassword(authEmail.trim(), authPassword);
      setAuthOpen(false); setAuthPassword('');
    } catch (err) {
      setAuthErr(err instanceof Error ? err.message.replace(/^Firebase:\s*/, '') : 'Something went wrong.');
    } finally { setAuthBusy(false); }
  };
  const googleAuth = async () => {
    setAuthErr(null); setAuthBusy(true);
    try { await signInWithGoogle(); setAuthOpen(false); }
    catch (err) { setAuthErr(err instanceof Error ? err.message.replace(/^Firebase:\s*/, '') : 'Google sign-in failed.'); }
    finally { setAuthBusy(false); }
  };
  const [visOverride, setVisOverride] = useState<Record<string, 'shared' | 'private'>>({});
  const [watchId, setWatchId] = useState<string | null>(null);
  const [txOpen, setTxOpen] = useState<string | null>(null);
  const [txCache, setTxCache] = useState<Record<string, { text?: string; loading?: boolean; error?: boolean }>>({});
  const txScrolled = useRef<string | null>(null);
  const watchScrolled = useRef<string | null>(null);
  // Collections / queue / history
  const [userCollections, setUserCollections] = useState<UserCollection[]>([]);
  const [saveOpenFor, setSaveOpenFor] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState<Record<string, string>>({});
  const [newColName, setNewColName] = useState('');
  const loadCollections = async () => { try { setUserCollections(await listCollections()); } catch { /* ignore */ } };
  const snippetOf = (m: MinedEvent) => ({ eventId: m.id, videoId: m.videoId, title: m.title, summary: m.summary, channelId: m.channelId, channelTitle: m.channelTitle, sourceTimestampSec: m.sourceTimestampSec, sourceTimestampUrl: m.sourceTimestampUrl, eventDateKey: m.eventDateKey });
  const flashSave = (id: string, msg: string) => { setSaveMsg((s) => ({ ...s, [id]: msg })); setSaveOpenFor(null); setTimeout(() => setSaveMsg((s) => { const n = { ...s }; delete n[id]; return n; }), 2500); };
  const saveToQueue = async (m: MinedEvent) => {
    try { const r = await addToQueue(snippetOf(m)); flashSave(m.id, r === 'exists' ? 'Already in Queue' : 'Added to Queue ✓'); }
    catch { flashSave(m.id, 'Sign in to save'); }
  };
  const saveToCol = async (m: MinedEvent, colId: string, colName: string) => {
    try { const r = await addToCollection(colId, snippetOf(m)); flashSave(m.id, r === 'exists' ? `Already in ${colName}` : `Added to ${colName} ✓`); }
    catch { flashSave(m.id, 'Sign in to save'); }
  };
  const createAndSave = async (m: MinedEvent) => {
    const name = newColName.trim(); if (!name) return;
    try { const id = await createCollection(name); await addToCollection(id, snippetOf(m)); setNewColName(''); await loadCollections(); flashSave(m.id, `Added to ${name} ✓`); }
    catch { flashSave(m.id, 'Sign in to save'); }
  };
  const [hiddenChannels, setHiddenChannels] = useState<Set<string>>(new Set());
  const toggleChannel = (id: string) => setHiddenChannels((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const chKey = (m: { channelId?: string; channelTitle?: string }) => m.channelId || m.channelTitle || '';
  const [showGregorian, setShowGregorian] = useState(false);
  // Zoom scales the fonts + event-bar sizing (and how many lanes fit) — the 7×4 grid structure is
  // unchanged. Lower zoom = smaller text + more events visible; higher zoom = larger + fewer.
  const [zoom, setZoom] = useState(1);
  const laneH = Math.round(28 * zoom);
  const dayRowTop = Math.round(44 * zoom);
  const maxLanes = Math.min(14, Math.max(3, Math.round(5 / zoom)));
  const barFontPx = Math.max(9, Math.round(13 * zoom));
  const dayNumPx = Math.round(20 * zoom);

  const loadTranscript = async (videoId: string) => {
    if (txCache[videoId]?.text != null || txCache[videoId]?.loading) return;
    setTxCache((p) => ({ ...p, [videoId]: { loading: true } }));
    try {
      const authInst = await getAuthInstance();
      const user = authInst?.currentUser;
      const headers: Record<string, string> = user ? { Authorization: `Bearer ${await user.getIdToken()}` } : {};
      const res = await fetch(`${functionsOrigin()}/getTranscript?videoId=${encodeURIComponent(videoId)}`, { headers });
      if (!res.ok) throw new Error(String(res.status));
      const j = await res.json();
      setTxCache((p) => ({ ...p, [videoId]: { text: j.text || '' } }));
    } catch {
      setTxCache((p) => ({ ...p, [videoId]: { error: true } }));
    }
  };

  const toggleShare = async (e: CalendarEvent) => {
    if (!e.id) return;
    const cur = visOverride[e.id] ?? e.visibility ?? 'private';
    const next = cur === 'shared' ? 'private' : 'shared';
    setVisOverride((prev) => ({ ...prev, [e.id!]: next }));
    try {
      await updateEvent(e.id, { visibility: next });
    } catch (err) {
      setVisOverride((prev) => ({ ...prev, [e.id!]: cur })); // revert on failure
      console.error('[ThirteenCalendar] toggle share failed', err);
    }
  };

  const months = useMemo(() => Array.from({ length: 13 }, (_, i) => getMonth13(galacticYear, i + 1)), [galacticYear]);
  const outOfTime = useMemo(() => day13ToGregorian(galacticYear, 0, 0), [galacticYear]);
  const today13 = useMemo(() => getToday13(), []);

  // Load ONLY the currently-viewed moon's 28-day Gregorian span — not the whole galactic year.
  const moon = months[moonIndex - 1];
  const moonStart = moon.days[0].gregorianDate;
  const moonEnd = moon.days[moon.days.length - 1].gregorianDate;
  const eventsByDate = useEventsForRange(moonStart, moonEnd);

  useEffect(() => {
    const monthKeys = Array.from(new Set(moon.days.map((d) => keyOf(d.gregorianDate).slice(0, 7))));
    let alive = true;
    void fetchMinedForMonths(monthKeys).then((m) => { if (alive) setMined(m); });
    return () => { alive = false; };
  }, [moonStart, moonEnd]);

  // Channel metadata (per-channel event colour, title, thumbnail) — public, loaded once.
  useEffect(() => {
    let alive = true;
    void fetch(`${functionsOrigin()}/listPublicChannels`)
      .then((r) => r.json())
      .then((j) => {
        if (!alive) return;
        const map: Record<string, { color?: string; title?: string; handle?: string; thumbnailUrl?: string }> = {};
        for (const c of (j.channels || [])) map[c.channelId] = { color: c.color || undefined, title: c.title || undefined, handle: c.handle || undefined, thumbnailUrl: c.thumbnailUrl || undefined };
        setChannelMeta(map);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  useEffect(() => { if (email) void loadCollections(); else setUserCollections([]); }, [email]); // eslint-disable-line react-hooks/exhaustive-deps
  const plasma = PLASMA_NAMES.slice(0, 7);

  const calFor = (d: Date) => eventsByDate[isoKey(d)] || eventsByDate[keyOf(d)] || [];
  const minedFor = (d: Date) => {
    const dk = keyOf(d);
    return mined.filter((m) => !hiddenChannels.has(chKey(m)) && m.eventDateKey === dk);
  };
  const MINED_DEFAULT = '#f59e0b';
  const channelColorOf = (m: { channelId?: string; channelTitle?: string }) => channelMeta[chKey(m)]?.color || MINED_DEFAULT;

  // Header filter = ALL public channels (so a newly-registered channel appears immediately, even
  // before it has events in the visible moon), unioned with any channel present in the loaded events.
  const channels = useMemo(() => {
    const map = new Map<string, { title: string; color?: string }>();
    for (const [id, meta] of Object.entries(channelMeta)) map.set(id, { title: meta.title || id, color: meta.color });
    for (const m of mined) { const id = chKey(m); if (id && !map.has(id)) map.set(id, { title: m.channelTitle || id, color: channelMeta[id]?.color }); }
    const arr = Array.from(map, ([id, v]) => ({ id, title: v.title, color: v.color }));
    // Disambiguate distinct channels that happen to share a title.
    const counts = new Map<string, number>();
    for (const c of arr) counts.set(c.title, (counts.get(c.title) || 0) + 1);
    for (const c of arr) if ((counts.get(c.title) || 0) > 1) c.title = `${c.title} (${c.id.slice(0, 6)})`;
    return arr.sort((a, b) => a.title.localeCompare(b.title));
  }, [mined, channelMeta]);

  const flat = useMemo<Flat[]>(() => {
    const out: Flat[] = [];
    let gi = 0;
    for (const mon of months) for (const d of mon.days) out.push({ gi: gi++, moon: mon.number, day: d.day, greg: d.gregorianDate, d13: d });
    return out;
  }, [months]);
  const keyToGi = useMemo(() => { const m = new Map<string, number>(); for (const f of flat) m.set(keyOf(f.greg), f.gi); return m; }, [flat]);
  // "YYYY-MM" -> [firstGi, lastGi] of that Gregorian month, so a month-precision event spans it.
  const monthGi = useMemo(() => {
    const m = new Map<string, [number, number]>();
    for (const f of flat) { const mk = keyOf(f.greg).slice(0, 7); const cur = m.get(mk); if (!cur) m.set(mk, [f.gi, f.gi]); else cur[1] = f.gi; }
    return m;
  }, [flat]);
  const weeks = useMemo(() => { const w: Flat[][] = []; for (let i = 0; i < flat.length; i += 7) w.push(flat.slice(i, i + 7)); return w; }, [flat]);

  const todayGi = (today13.year === galacticYear && !today13.isOutOfTime)
    ? (today13.month - 1) * 28 + (today13.day - 1) : -1;

  const spans = useMemo<Span[]>(() => {
    const out: Span[] = [];
    const seen = new Map<string, CalendarEvent>();
    for (const list of Object.values(eventsByDate)) for (const e of list) if (!seen.has(e.id)) seen.set(e.id, e);
    for (const e of seen.values()) {
      const endDate = e.end && e.end.getTime() > e.start.getTime() ? new Date(e.end.getTime() - 1000) : (e.end || e.start);
      let startGi = keyToGi.get(keyOf(e.start));
      let endGi = keyToGi.get(keyOf(endDate));
      if (startGi == null && endGi == null) continue;
      if (startGi == null) startGi = 0;
      if (endGi == null) endGi = flat.length - 1;
      if (endGi < startGi) endGi = startGi;
      out.push({ id: e.id, title: e.title || '(untitled)', startGi, endGi, kind: 'cal', openGi: startGi });
    }
    for (const m of mined) {
      if (hiddenChannels.has(chKey(m))) continue; // channel filter
      const gi = keyToGi.get(m.eventDateKey || '');
      if (gi == null) continue;
      out.push({ id: m.id, title: m.title || '(mined)', startGi: gi, endGi: gi, kind: 'mined', openGi: gi, color: channelColorOf(m) });
    }
    return out;
  }, [eventsByDate, mined, keyToGi, flat.length, hiddenChannels, channelMeta]);

  const weekLayouts = useMemo(() => weeks.map((week, wi) => {
    const ws = wi * 7, we = ws + week.length - 1;
    const segs: Seg[] = [];
    for (const s of spans) {
      if (s.endGi < ws || s.startGi > we) continue;
      segs.push({ span: s, colStart: Math.max(s.startGi, ws) - ws, colEnd: Math.min(s.endGi, we) - ws, contL: s.startGi < ws, contR: s.endGi > we, lane: 0 });
    }
    segs.sort((a, b) => a.colStart - b.colStart || (b.colEnd - b.colStart) - (a.colEnd - a.colStart));
    const laneEnd: number[] = [];
    for (const seg of segs) {
      let lane = 0;
      while (lane < laneEnd.length && laneEnd[lane] >= seg.colStart) lane++;
      seg.lane = lane; laneEnd[lane] = seg.colEnd;
    }
    const shown = segs.filter((s) => s.lane < maxLanes);
    // Per-day overflow: how many events are hidden in each column (day) of this week.
    const colOverflow = new Array(week.length).fill(0) as number[];
    for (const s of segs) if (s.lane >= maxLanes) for (let c = s.colStart; c <= s.colEnd && c < colOverflow.length; c++) colOverflow[c] += 1;
    return { shown, lanes: Math.min(laneEnd.length, maxLanes), colOverflow, ws };
  }), [weeks, spans, maxLanes]);

  const openDay = (gi: number, watchMined?: string) => { const f = flat[gi]; if (f) { setSelected({ d13: f.d13, cal: calFor(f.greg), mined: minedFor(f.greg) }); setWatchId(watchMined ?? null); if (watchMined && email) { const ev = mined.find((x) => x.id === watchMined); if (ev) void recordHistory(snippetOf(ev)); } } };

  const barCls = (kind: 'cal' | 'mined') =>
    kind === 'cal'
      ? 'bg-sky-500 hover:bg-sky-400 text-white font-semibold ring-1 ring-black/30 shadow-sm'
      : 'bg-amber-400 hover:bg-amber-300 text-black font-semibold ring-1 ring-black/30 shadow-sm';

  // Current moon + navigation (wraps across galactic years). `moon` is defined above (data loading).
  const moonWeeks = weeks.slice((moonIndex - 1) * 4, (moonIndex - 1) * 4 + 4);
  const moonRange = `${moon.days[0].gregorianDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${moon.days[moon.days.length - 1].gregorianDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  const goPrev = () => { if (moonIndex > 1) setMoonIndex(moonIndex - 1); else { setGalacticYear((y) => y - 1); setMoonIndex(13); } };
  const goNext = () => { if (moonIndex < 13) setMoonIndex(moonIndex + 1); else { setGalacticYear((y) => y + 1); setMoonIndex(1); } };
  const goToday = () => { const t = getToday13(); setGalacticYear(getCurrentGalacticYear()); setMoonIndex(t.isOutOfTime ? 13 : t.month); };

  return (
    <div className="flex h-screen flex-col bg-[#050416] text-white">
      <div className="mx-auto flex w-full max-w-[1600px] flex-1 flex-col overflow-hidden p-3 sm:p-4">
        {/* header */}
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">{moon.glyph}</span>
              <h1 className="text-2xl font-semibold tracking-tight">Moon {moon.number} · {moon.name}</h1>
            </div>
            <p className="mt-1 text-xl font-semibold text-white/90">Galactic Year {galacticYear} · {moonRange}</p>
          </div>
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            {email ? (
              <>
                <span className="max-w-[10rem] truncate rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-xs text-white/70" title={email}>{email}</span>
                <button type="button" onClick={() => void signOut()} className="rounded-md border border-white/15 px-2.5 py-1.5 text-sm hover:bg-white/10">Sign out</button>
              </>
            ) : (
              <button type="button" onClick={() => { setAuthMode('join'); setAuthErr(null); setAuthOpen(true); }} className="rounded-md border border-amber-400/50 bg-amber-500/20 px-3 py-1.5 text-sm font-semibold text-amber-100 hover:bg-amber-500/30">Join free</button>
            )}
            <span className="mx-1 hidden h-5 w-px bg-white/15 sm:block" />
            {channels.length > 1 || channels.some((c) => hiddenChannels.has(c.id)) ? (
              <>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="mr-0.5 text-xs uppercase tracking-wider text-white/45">Channels</span>
                  {channels.map((c) => {
                    const on = !hiddenChannels.has(c.id);
                    const col = c.color || '#f59e0b';
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => toggleChannel(c.id)}
                        aria-pressed={on}
                        title={on ? `Hide ${c.title} events` : `Show ${c.title} events`}
                        className={`inline-flex max-w-[12rem] items-center gap-1.5 truncate rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${on ? '' : 'border-white/15 bg-white/5 text-white/60 line-through hover:bg-white/10'}`}
                        style={on ? { backgroundColor: `${col}26`, borderColor: `${col}80`, color: '#fff' } : undefined}
                      >
                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: on ? col : '#6b7280' }} />
                        <span className="truncate">{c.title}</span>
                      </button>
                    );
                  })}
                </div>
                <span className="mx-1 hidden h-5 w-px bg-white/15 sm:block" />
              </>
            ) : null}
            <Link to="/collections" className="rounded-md border border-white/15 px-3 py-1.5 text-sm hover:bg-white/10">◆ Collections</Link>
            <Link to="/dashboard/skin" className="rounded-md border border-white/15 px-3 py-1.5 text-sm hover:bg-white/10">← Dashboard</Link>
            {isPaul ? <Link to="/admin" className="rounded-md border border-emerald-400/40 bg-emerald-500/15 px-3 py-1.5 text-sm text-emerald-100 hover:bg-emerald-500/25">Admin</Link> : null}
            <span className="mx-1 hidden h-5 w-px bg-white/15 sm:block" />
            <button onClick={goPrev} className="rounded-md border border-white/15 px-3 py-1.5 text-sm hover:bg-white/10">← Moon</button>
            <button onClick={goToday} className="rounded-md border border-amber-400/40 bg-amber-500/15 px-3 py-1.5 text-sm text-amber-100 hover:bg-amber-500/25">Today</button>
            <button onClick={goNext} className="rounded-md border border-white/15 px-3 py-1.5 text-sm hover:bg-white/10">Moon →</button>
          </div>
        </div>

        {/* legend */}
        <div className="mb-2 flex flex-wrap items-center gap-4 text-sm text-white/85">
          <span className="flex items-center gap-1.5"><span className="h-3 w-5 rounded-sm bg-sky-500" /> Calendar event</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-5 rounded-sm bg-amber-400" /> Mined timeline event</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full ring-2 ring-white" /> Today</span>
          <span className="text-white/60">Events stretch across the days they span; click a bar or day for details.</span>
          <div className="ml-auto flex items-center gap-1 rounded-full border border-white/20 px-1 py-0.5" title="Zoom (font size — grid stays the same)">
            <button type="button" aria-label="Zoom out" onClick={() => setZoom((z) => Math.max(0.7, Math.round((z - 0.1) * 10) / 10))} className="rounded-full px-2 text-base leading-none text-white/80 hover:bg-white/10">−</button>
            <button type="button" onClick={() => setZoom(1)} className="min-w-[2.75rem] text-center text-xs tabular-nums text-white/70 hover:text-white" title="Reset zoom">{Math.round(zoom * 100)}%</button>
            <button type="button" aria-label="Zoom in" onClick={() => setZoom((z) => Math.min(1.5, Math.round((z + 0.1) * 10) / 10))} className="rounded-full px-2 text-base leading-none text-white/80 hover:bg-white/10">+</button>
          </div>
          <button
            type="button"
            onClick={() => setShowGregorian((s) => !s)}
            aria-pressed={showGregorian}
            title="Show the Gregorian date in each day"
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${showGregorian ? 'border-sky-400/50 bg-sky-500/20 text-sky-100 hover:bg-sky-500/30' : 'border-white/20 text-white/70 hover:bg-white/10'}`}
          >
            {showGregorian ? '✓ ' : ''}Gregorian dates
          </button>
        </div>

        {/* one large moon — fills the viewport, 4 equal-height weeks */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-white/20 bg-[#0d0b2a]">
          <div className="grid shrink-0 grid-cols-7 border-b border-white/20 bg-white/[0.06] text-center text-sm font-semibold uppercase tracking-widest text-white/80">
            {plasma.map((p) => <div key={p.short} className="py-2.5">{p.short}</div>)}
          </div>
          {moonWeeks.map((week, wi) => {
            const weekIndex = (moonIndex - 1) * 4 + wi;
            const layout = weekLayouts[weekIndex];
            return (
              <div key={weekIndex} className="relative min-h-0 flex-1 overflow-hidden border-b border-white/15 last:border-b-0">
                <div className="grid h-full grid-cols-7">
                  {week.map((f) => {
                    const isToday = f.gi === todayGi;
                    return (
                      <button
                        key={f.gi}
                        onClick={() => openDay(f.gi)}
                        title={f.greg.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                        className="group relative border-r border-white/15 text-left last:border-r-0 hover:bg-white/[0.06]"
                      >
                        <span className={`absolute left-2 top-2 flex h-10 min-w-10 items-center justify-center rounded-full px-1.5 font-bold tabular-nums ${isToday ? 'bg-amber-500 text-black ring-2 ring-white' : 'text-white group-hover:text-white'}`} style={{ fontSize: dayNumPx }}>
                          {f.day}
                        </span>
                        {showGregorian ? (
                          <span className="absolute right-2 top-3 text-xs font-medium tabular-nums text-white/55">
                            {f.greg.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
                <div className="pointer-events-none absolute inset-0">
                  {layout.shown.map((seg, i) => {
                    const isMined = seg.span.kind === 'mined';
                    const col = seg.span.color || '#f59e0b';
                    return (
                    <button
                      key={`${seg.span.id}-${i}`}
                      onClick={() => openDay(seg.span.openGi, isMined ? seg.span.id : undefined)}
                      title={seg.span.title}
                      className={`pointer-events-auto absolute flex items-center overflow-hidden text-ellipsis whitespace-nowrap rounded-md px-2 font-semibold shadow-sm ring-1 ring-black/30 transition-opacity hover:opacity-90 ${isMined ? '' : 'bg-sky-500 text-white'} ${seg.contL ? 'rounded-l-none' : ''} ${seg.contR ? 'rounded-r-none' : ''}`}
                      style={{
                        ...(isMined ? { backgroundColor: col, color: textOn(col) } : {}),
                        left: `calc(${(seg.colStart / 7) * 100}% + 3px)`,
                        width: `calc(${((seg.colEnd - seg.colStart + 1) / 7) * 100}% - 6px)`,
                        top: dayRowTop + seg.lane * laneH,
                        height: laneH - 4,
                        fontSize: barFontPx,
                      }}
                    >
                      {seg.contL ? '‹ ' : ''}{seg.span.title}
                    </button>
                    );
                  })}
                  {layout.colOverflow.map((n, col) => n > 0 ? (
                    <button
                      key={`ov-${col}`}
                      type="button"
                      onClick={() => openDay(layout.ws + col)}
                      title={`Show all events on this day`}
                      className="pointer-events-auto absolute rounded px-1.5 text-[12px] font-semibold text-white/80 hover:bg-white/10 hover:text-white"
                      style={{ left: `calc(${(col / 7) * 100}% + 3px)`, top: dayRowTop + maxLanes * laneH, fontSize: Math.max(10, barFontPx - 1) }}
                    >
                      +{n} more
                    </button>
                  ) : null)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Day Out of Time — the green day at the year boundary, shown on the last moon */}
        {moonIndex === 13 ? (
          <button
            onClick={() => setSelected({ d13: { month: 0, day: 0, year: galacticYear, isOutOfTime: true, gregorianDate: outOfTime, weekday: -1 } as Day13, cal: calFor(outOfTime), mined: minedFor(outOfTime) })}
            className="mt-3 flex w-full items-center gap-2 rounded-xl border border-fuchsia-400/20 bg-fuchsia-500/[0.06] px-3 py-2.5 text-left text-sm hover:bg-fuchsia-500/[0.12]"
          >
            <span>✴</span>
            <span className="font-medium">Day Out of Time</span>
            <span className="text-white/50">{outOfTime.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })} · green day · no moon/day</span>
          </button>
        ) : null}
      </div>

      {/* day detail */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setWatchId(null); setTxOpen(null); watchScrolled.current = null; } }}>
        <DialogContent className="flex max-h-[85vh] max-w-lg flex-col overflow-hidden border-white/10 bg-[#0b0f26] text-white">
          <DialogHeader>
            <DialogTitle className="text-base">
              {selected?.d13.isOutOfTime ? 'Day Out of Time' : selected ? `${MONTH_NAMES[selected.d13.month - 1]?.glyph} ${MONTH_NAMES[selected.d13.month - 1]?.name} ${selected.d13.day}` : ''}
              {selected ? <span className="ml-2 text-xs font-normal text-white/50">{selected.d13.gregorianDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span> : null}
            </DialogTitle>
          </DialogHeader>
          {selected ? (
            <div className="-mr-2 flex-1 space-y-3 overflow-y-auto pr-2">
              {selected.cal.length === 0 && selected.mined.length === 0 ? (
                <p className="text-sm text-white/50">No events on this day.</p>
              ) : null}
              {selected.cal.length ? (
                <div>
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-sky-300">Calendar</div>
                  {selected.cal.map((e) => {
                    const isShared = (e.id ? visOverride[e.id] : undefined) ?? e.visibility ?? 'private';
                    return (
                    <div key={e.id} className="mb-1 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium">{e.title}</div>
                          {!e.allDay ? <div className="text-xs text-white/50">{e.start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}{e.location ? ` · ${e.location}` : ''}</div> : <div className="text-xs text-white/50">All day{e.location ? ` · ${e.location}` : ''}</div>}
                        </div>
                        {isPaul ? (
                          <button
                            type="button"
                            onClick={() => toggleShare(e)}
                            title={isShared === 'shared' ? 'Public — visible to everyone. Click to make private.' : 'Private — only you. Click to share publicly.'}
                            className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${isShared === 'shared' ? 'border-emerald-400/40 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25' : 'border-white/15 text-white/55 hover:bg-white/10'}`}
                          >
                            {isShared === 'shared' ? <><Globe className="h-3 w-3" /> Public</> : <><Lock className="h-3 w-3" /> Private</>}
                          </button>
                        ) : isShared === 'shared' ? (
                          <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-100"><Globe className="h-3 w-3" /> Public</span>
                        ) : null}
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : null}
              {selected.mined.length ? (
                <div>
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-300">Mined timeline</div>
                  <div className="space-y-1.5">
                    {selected.mined.map((m) => {
                      const t = m.sourceTimestampSec;
                      const col = channelColorOf(m);
                      const ytUrl = m.sourceTimestampUrl
                        || (m.videoId ? `https://www.youtube.com/watch?v=${m.videoId}${t != null ? `&t=${Math.floor(t)}s` : ''}` : null);
                      const watching = watchId === m.id;
                      return (
                      <div
                        key={m.id}
                        ref={m.id && m.id === watchId ? (el) => {
                          if (el && watchScrolled.current !== m.id) {
                            watchScrolled.current = m.id!;
                            setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 70);
                          }
                        } : undefined}
                        className={`rounded-lg border transition-all ${watching ? 'scale-[1.01] bg-white/[0.08] px-4 py-3.5 shadow-xl' : 'border-white/15 bg-white/[0.05] px-3 py-2.5'} text-sm`}
                        style={watching ? { borderColor: `${col}80`, boxShadow: `0 0 0 1px ${col}55, inset 4px 0 0 ${col}, 0 12px 28px rgba(0,0,0,0.45)` } : undefined}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          {watching ? <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-black" style={{ backgroundColor: col }}>★ Featured</span> : null}
                          <span className={`font-semibold text-white ${watching ? 'text-lg' : 'text-base'}`}>{m.title}</span>
                          {typeof m.confidence === 'number' ? <Badge variant="outline" className="border-white/25 text-[11px] text-white/80">{m.confidence.toFixed(2)}</Badge> : null}
                        </div>
                        {m.summary ? <div className="mt-1 text-sm text-white/75">{m.summary}</div> : null}
                        {m.sourceQuote ? <div className="mt-1.5 border-l-2 border-amber-400/60 pl-2 text-sm italic text-white/65">“{m.sourceQuote}”</div> : null}
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {m.channelTitle ? <span className="text-xs text-white/60">{m.channelTitle}</span> : null}
                          {m.videoId ? (
                            <button
                              type="button"
                              onClick={() => { const open = !watching; setWatchId(open ? m.id : null); if (open && email) void recordHistory(snippetOf(m)); }}
                              className="inline-flex items-center gap-1 rounded-full border border-amber-400/50 bg-amber-500/20 px-2.5 py-1 text-xs font-semibold text-amber-100 hover:bg-amber-500/30"
                            >
                              {watching ? 'Hide video' : `▶ Watch${t != null ? ` at ${fmtClock(t)}` : ''}`}
                            </button>
                          ) : null}
                          {ytUrl ? (
                            <a
                              href={ytUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 rounded-full border border-white/20 px-2.5 py-1 text-xs text-white/75 hover:bg-white/10"
                            >
                              YouTube ↗
                            </a>
                          ) : null}
                          {m.videoId ? (
                            <button
                              type="button"
                              onClick={() => { const open = txOpen === m.id; setTxOpen(open ? null : m.id); if (!open && m.videoId) { txScrolled.current = null; void loadTranscript(m.videoId); } }}
                              className="inline-flex items-center gap-1 rounded-full border border-white/20 px-2.5 py-1 text-xs text-white/75 hover:bg-white/10"
                            >
                              {txOpen === m.id ? 'Hide transcript' : '≡ Transcript'}
                            </button>
                          ) : null}
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => setSaveOpenFor(saveOpenFor === m.id ? null : m.id)}
                              className="inline-flex items-center gap-1 rounded-full border border-white/20 px-2.5 py-1 text-xs text-white/75 hover:bg-white/10"
                            >
                              ＋ Save
                            </button>
                            {saveOpenFor === m.id ? (
                              <div className="absolute left-0 z-20 mt-1 w-56 rounded-md border border-white/15 bg-[#0f1330] p-1.5 shadow-xl">
                                <button type="button" onClick={() => void saveToQueue(m)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-white/10">▸ Add to Queue</button>
                                {userCollections.filter((c) => c.kind !== 'queue').map((c) => (
                                  <button key={c.id} type="button" onClick={() => void saveToCol(m, c.id, c.name)} className="flex w-full items-center gap-2 truncate rounded px-2 py-1.5 text-left text-xs hover:bg-white/10">{c.name}</button>
                                ))}
                                <div className="my-1 h-px bg-white/10" />
                                <div className="flex items-center gap-1 px-1 pb-0.5">
                                  <input value={newColName} onChange={(e) => setNewColName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void createAndSave(m); }} placeholder="New collection…" className="w-full rounded border border-white/15 bg-white/[0.03] px-2 py-1 text-xs text-white outline-none focus:border-amber-400/50" />
                                  <button type="button" onClick={() => void createAndSave(m)} className="rounded bg-amber-500/20 px-2 py-1 text-xs font-semibold text-amber-100 hover:bg-amber-500/30">＋</button>
                                </div>
                              </div>
                            ) : null}
                          </div>
                          {saveMsg[m.id] ? <span className="text-[11px] font-medium text-emerald-300">{saveMsg[m.id]}</span> : null}
                        </div>
                        {watching && m.videoId ? (
                          <div className="mt-2">
                            <div className="mb-1 flex items-center gap-1.5 text-[11px] text-white/50">
                              <span className="text-amber-300">▶</span>
                              {m.channelTitle ? <span className="font-medium text-white/70">{m.channelTitle}</span> : <span className="text-white/70">Source video</span>}
                              {t != null ? <span>· from {fmtClock(t)}</span> : null}
                            </div>
                            <div className="overflow-hidden rounded-md border border-white/10" style={{ aspectRatio: '16 / 9' }}>
                              <iframe
                                title={m.title || 'source video'}
                                src={`https://www.youtube-nocookie.com/embed/${m.videoId}?start=${Math.floor(t || 0)}&autoplay=1&rel=0`}
                                className="h-full w-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </div>
                            {/* Like / comment natively on YouTube — opens the video at this moment */}
                            <div className="mt-2 flex flex-wrap gap-2">
                              <a
                                href={ytUrl || `https://www.youtube.com/watch?v=${m.videoId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 rounded-md border border-white/20 bg-white/[0.04] px-3 py-1.5 text-sm font-medium text-white/90 hover:bg-white/10"
                              >
                                👍 Like on YouTube <span className="text-white/50">↗</span>
                              </a>
                              <a
                                href={ytUrl || `https://www.youtube.com/watch?v=${m.videoId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 rounded-md border border-white/20 bg-white/[0.04] px-3 py-1.5 text-sm font-medium text-white/90 hover:bg-white/10"
                              >
                                💬 Comment on YouTube <span className="text-white/50">↗</span>
                              </a>
                            </div>
                          </div>
                        ) : null}
                        {txOpen === m.id && m.videoId ? (() => {
                          const tc = txCache[m.videoId];
                          if (!tc || tc.loading) return <div className="mt-2 rounded-md border border-white/10 bg-black/40 p-2 text-xs text-white/60">Loading transcript…</div>;
                          if (tc.error || !tc.text) return <div className="mt-2 rounded-md border border-white/10 bg-black/40 p-2 text-xs text-white/60">Transcript unavailable.</div>;
                          const target = m.sourceTimestampSec ?? -1;
                          const parsed = tc.text.split('\n').map((ln) => { const mm = ln.match(/^\[(\d+)\]\s?(.*)$/); return mm ? { sec: Number(mm[1]), text: mm[2] } : { sec: null as number | null, text: ln }; });
                          let activeIdx = -1, best = Infinity;
                          if (target >= 0) parsed.forEach((p, i) => { if (p.sec != null) { const d = Math.abs(p.sec - target); if (d < best) { best = d; activeIdx = i; } } });
                          return (
                            <div className="mt-2 max-h-64 overflow-y-auto rounded-md border border-white/10 bg-black/40 p-2 text-[13px] leading-relaxed">
                              {parsed.map((p, i) => (
                                <div
                                  key={i}
                                  ref={i === activeIdx ? (el) => { if (el && txScrolled.current !== m.id) { txScrolled.current = m.id!; el.scrollIntoView({ block: 'center' }); } } : undefined}
                                  className={`flex gap-2 rounded px-1 py-0.5 ${i === activeIdx ? 'bg-amber-400/25 text-white' : 'text-white/70'}`}
                                >
                                  {p.sec != null
                                    ? <a href={`https://www.youtube.com/watch?v=${m.videoId}&t=${p.sec}s`} target="_blank" rel="noopener noreferrer" className="shrink-0 tabular-nums text-amber-300/80 hover:text-amber-200">{fmtClock(p.sec)}</a>
                                    : <span className="w-9 shrink-0" />}
                                  <span>{p.text}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })() : null}
                      </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Sign in / Join free */}
      <Dialog open={authOpen} onOpenChange={(o) => { if (!o) { setAuthOpen(false); setAuthErr(null); } }}>
        <DialogContent className="max-w-sm border-white/10 bg-[#0b0f26] text-white">
          <DialogHeader>
            <DialogTitle className="text-lg">{authMode === 'join' ? 'Join free' : 'Welcome back'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitAuth} className="space-y-3">
            <p className="text-sm text-white/55">{authMode === 'join' ? 'Create a free account to save events and sync your calendar.' : 'Sign in to your account.'}</p>
            <button type="button" onClick={googleAuth} disabled={authBusy} className="flex w-full items-center justify-center gap-2 rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm font-medium hover:bg-white/10 disabled:opacity-50">
              <span className="text-base">G</span> Continue with Google
            </button>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/35"><span className="h-px flex-1 bg-white/10" />or<span className="h-px flex-1 bg-white/10" /></div>
            <input type="email" required value={authEmail} onChange={(e) => setAuthEmail(e.target.value)} placeholder="you@email.com" autoComplete="email" className="w-full rounded-md border border-white/15 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-amber-400/50" />
            <input type="password" required value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} placeholder="Password" autoComplete={authMode === 'join' ? 'new-password' : 'current-password'} minLength={6} className="w-full rounded-md border border-white/15 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-amber-400/50" />
            {authErr ? <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">{authErr}</div> : null}
            <button type="submit" disabled={authBusy} className="w-full rounded-md border border-amber-400/50 bg-amber-500/20 px-3 py-2 text-sm font-semibold text-amber-100 hover:bg-amber-500/30 disabled:opacity-50">
              {authBusy ? 'Please wait…' : authMode === 'join' ? 'Create account' : 'Sign in'}
            </button>
            <div className="text-center text-xs text-white/50">
              {authMode === 'join' ? 'Already have an account?' : 'New here?'}{' '}
              <button type="button" onClick={() => { setAuthMode(authMode === 'join' ? 'signin' : 'join'); setAuthErr(null); }} className="font-medium text-amber-300 hover:underline">
                {authMode === 'join' ? 'Sign in' : 'Join free'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
